import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    PhoneCallHandler,
    callStatusAtom,
} from '@/caregiverSpace/features/consultation/call-confirmation';
import { useAtomValue } from 'jotai';

export const PhoneCallContext = createContext<{
    phoneCallHandler: null | PhoneCallHandler;
    setPhoneCallHandler: React.Dispatch<
        React.SetStateAction<PhoneCallHandler | null>
    >;
    callDuration?: number;
}>({
    phoneCallHandler: null,
    setPhoneCallHandler: () => {},
    callDuration: 0,
});

export const PhoneCallProvider = ({ children }: React.PropsWithChildren) => {
    const status = useAtomValue(callStatusAtom);
    const [callDuration, setCallDuration] = useState(0);
    const timer = useRef<number | null>(null);
    const [phoneCallHandler, setPhoneCallHandler] =
        useState<PhoneCallHandler | null>(null);

    useEffect(() => {
        const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
            await phoneCallHandler?.hangUp();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [phoneCallHandler]);

    useEffect(() => {
        if (status == 'ongoing') {
            timer.current = window.setInterval(() => {
                setCallDuration((prev) => prev + 1);
            }, 1000);
        }
        if (status !== 'ongoing' && timer.current !== null) {
            clearInterval(timer.current);
            timer.current = null;
            setCallDuration(0);
        }
    }, [status]);

    return (
        <PhoneCallContext.Provider
            value={{
                phoneCallHandler,
                setPhoneCallHandler,
                callDuration: callDuration,
            }}
        >
            {children}
        </PhoneCallContext.Provider>
    );
};

export const usePhoneCallContext = () => useContext(PhoneCallContext);
